import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';


interface AuthContextType {
  currentUser: User | null;
  isAdmin: boolean;
  loading: boolean;
  captainTeamId: string;
  authDivision: string;
  isTeamMember: boolean;
  isSub: boolean;
  subName: string;
  isCaster: boolean;
  casterName: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authDivision, setAuthDivision] = useState('test');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [isSub, setIsSub] = useState(false);
  const [subName, setSubName] = useState('');
  const [isCaster, setIsCaster] = useState(false);
  const [casterName, setCasterName] = useState('');
  const [loading, setLoading] = useState(true);
  const [captainTeamId, setCaptainTeamId] = useState('');
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // When auth state changes, force a refresh of the ID token to get custom claims
        const idTokenResult = await user.getIdTokenResult(true);
        setIsAdmin(!!idTokenResult.claims.adminId);
        setIsTeamMember(!!idTokenResult.claims.isTeamMember);
        setIsSub(!!idTokenResult.claims.isSub);
        setCaptainTeamId(idTokenResult.claims.teamId as string);
        setAuthDivision(idTokenResult.claims.division as string);
        setSubName(idTokenResult.claims.subName as string);
        setIsCaster(!!idTokenResult.claims.isCaster);
        setCasterName(idTokenResult.claims.casterName as string || '');
      } else {
        setIsAdmin(false);
        setIsTeamMember(false);
        setIsSub(false);
        setSubName('');
        setIsCaster(false);
        setCasterName('');
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [auth, setCurrentUser, setIsAdmin, setLoading]);

  const value = { currentUser, isAdmin, loading, captainTeamId, authDivision, isTeamMember, isSub, subName, isCaster, casterName };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
